import React, { useState, useEffect } from 'react';
import { isValidEmail } from '../../../../../../Utils/Validations/validateEmail';
import { isValidCanadianPhone } from '../../../../../../Utils/Validations/validatePhone';
import { isValidCanadianPostalCode } from '../../../../../../Utils/Validations/validatePostalCode';
import apiClient from '../../../../../../Config/apiClient';
import { retrieve } from '../../../../../../Services/localStorageService';
import Loader from '../../../../../../Context/Loader/Loader';
import { FIELD_MAX_LENGTHS } from '../../../Utils/Constants';
import closeIcon from '../../../../../../Assets/images/close-icon.svg';
import { showErrorToast } from '../../../../../../Context/Toaster/Toaster';

const commonPath = process.env.REACT_APP_CC_COMMON_BASE_PATH;
const companyPath = process.env.REACT_APP_CC_COMPANY_BASE_PATH;
const signInPath = process.env.REACT_APP_CC_SIGNIN_BASE_PATH;

const CompanyProfileModal = ({ onClose }) => {
  const [formData, setFormData] = useState({
    companyName: '',
    corporateName: '',
    address1: '',
    address2: '',
    province: '',
    postalCode: '',
    phone: '',
    email: '',
  });

  const [formErrors, setFormErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [provinces, setProvinces] = useState([]);

  useEffect(() => {
    const companyName = retrieve('companyName');
    setFormData((prev) => ({
      ...prev,
      companyName: companyName || prev.companyName,
      email: '',
    }));

    const fetchProvinces = async () => {
      try {
        const res = await apiClient.get(`${commonPath}/list-provinces`);
        const fetchedProvinces = res.data?.data || [];
        setProvinces(fetchedProvinces);
      } catch (err) {
          const msg = err.response? err.response.data.message : err.message;
          console.error(msg);
          showErrorToast(msg);
      }
    };

    fetchProvinces();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    const limit = FIELD_MAX_LENGTHS[name] || (name === 'phone' ? FIELD_MAX_LENGTHS.contact : null);
    if (limit && value.length > limit) return;

    setFormData((prev) => ({ ...prev, [name]: value }));
    setFormErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const getFieldError = (name, value) => {
    if (
      ['companyName', 'corporateName', 'address1', 'postalCode', 'phone', 'province', 'email'].includes(name) &&
      !value.trim()
    ) {
      return name === 'province' ? 'Province is required.' : 'This field is required.';
    }

    if (name === 'phone' && value && !isValidCanadianPhone(value)) {
      return 'Enter a valid Canadian phone number.';
    }

    if (name === 'postalCode' && value && !isValidCanadianPostalCode(value)) {
      return 'Enter a valid Canadian postal code.';
    }

    if (name === 'email' && value && !isValidEmail(value)) {
      return 'Enter a valid email address.';
    }

    return '';
  };

  const validateForm = () => {
    const errors = {};
    Object.entries(formData).forEach(([name, value]) => {
      const error = getFieldError(name, value);
      if (error) errors[name] = error;
    });
    setFormErrors(errors);
    setSubmitted(true);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    const payload = {
      company_name: formData.companyName,
      corporate_name: formData.corporateName,
      address1: formData.address1,
      address2: formData.address2,
      province_id: provinces.find(p => p.province_name === formData.province)?.id || null,
      postal_code: formData.postalCode,
      contact_no: formData.phone,
      email_id: formData.email,
    };

    setLoading(true);
    try {
      const response = await apiClient.post(`${companyPath}/create`, payload);

      //Refresh the access token to get updated JWT with company info
      try {
        const refreshToken = localStorage.getItem('refreshToken');
        const refreshResponse = await apiClient.post(`${signInPath}/refresh`, {
          refreshToken
        });

        const newAccessToken = refreshResponse.data?.accessToken;
        if (newAccessToken) {
          localStorage.setItem('accessToken', newAccessToken);
        }
      } catch (err) {
          const msg = err.response? err.response.data.message : err.message;
          console.error(msg);
          showErrorToast(msg);
      }

      // Set dashboard flag and reload
      localStorage.setItem('showPendingAfterCreate', 'true');
      window.location.reload();

    } catch (err) {
      const msg = err.response? err.response.data.message : err.message;
      console.error(msg);
      showErrorToast(msg);
    } finally {
      setLoading(false);
    }
  };

  const showError = (field) => submitted && formErrors[field];

  return (
    <div className="cprofile-overlay">
      <div className="cprofile-content"style={{ position: 'relative', minHeight: '500px' }}>
        <div className="cpf-top">
          <button className="m-close" onClick={onClose} style={{ visibility: 'hidden' }}>
            <img src={closeIcon} alt="Close" />
          </button>
          <h2>Company Profile</h2>
        </div>

        <div className="cprofile-inner">
          <form className="cp-form" onSubmit={handleSubmit}>
            <div className="row">
              <div className="col-md-6">
                <div className="cp-split" style={{ position: "relative" }}>
                  <label className="fi-label">Company Name <span>*</span></label>
                  <input
                    type="text"
                    name="companyName"
                    value={formData.companyName}
                    onChange={handleChange}
                    className={`common-input ${showError('companyName') ? 'error' : ''}`}
                    maxLength={FIELD_MAX_LENGTHS.companyName}
                  />
                  {showError('companyName') && (
                    <span className="error-text active">{formErrors.companyName}</span>
                  )}
                </div>
              </div>

              <div className="col-md-6">
                <div className="cp-split" style={{ position: "relative" }}>
                  <label className="fi-label">Corporate Name <span>*</span></label>
                  <input
                    type="text"
                    name="corporateName"
                    value={formData.corporateName}
                    onChange={handleChange}
                    className={`common-input ${showError('corporateName') ? 'error' : ''}`}
                    maxLength={FIELD_MAX_LENGTHS.corporateName}
                  />
                  {showError('corporateName') && (
                    <span className="error-text active">{formErrors.corporateName}</span>
                  )}
                </div>
              </div>

              <div className="col-md-6">
                <div className="cp-split" style={{ position: "relative" }}>
                  <label className="fi-label">Address Line 1 <span>*</span></label>
                  <input
                    type="text"
                    name="address1"
                    value={formData.address1}
                    onChange={handleChange}
                    className={`common-input ${showError('address1') ? 'error' : ''}`}
                    maxLength={FIELD_MAX_LENGTHS.address}
                  />
                  {showError('address1') && (
                    <span className="error-text active">{formErrors.address1}</span>
                  )}
                </div>
              </div>

              <div className="col-md-6">
                <div className="cp-split">
                  <label className="fi-label">Address Line 2</label>
                  <input
                    type="text"
                    name="address2"
                    value={formData.address2}
                    onChange={handleChange}
                    className="common-input"
                    maxLength={FIELD_MAX_LENGTHS.address}
                  />
                </div>
              </div>

              <div className="col-md-6">
                <div className="cp-split" style={{ position: "relative" }}>
                  <label className="fi-label">Province <span>*</span></label>
                  <select
                    name="province"
                    value={formData.province}
                    onChange={handleChange}
                    className={`common-select ${showError('province') ? 'error' : ''}`}
                  >
                    <option value="">--Select--</option>
                    {provinces.map((prov) => (
                      <option key={prov.id} value={prov.province_name}>
                        {prov.province_name}
                      </option>
                    ))}
                  </select>
                  {showError('province') && (
                    <span className="error-text active">{formErrors.province}</span>
                  )}
                </div>
              </div>

              <div className="col-md-6">
                <div className="cp-split" style={{ position: "relative" }}>
                  <label className="fi-label">Postal Code <span>*</span></label>
                  <input
                    type="text"
                    name="postalCode"
                    value={formData.postalCode}
                    onChange={handleChange}
                    className={`common-input ${showError('postalCode') ? 'error' : ''}`}
                    maxLength={FIELD_MAX_LENGTHS.postalCode}
                  />
                  {showError('postalCode') && (
                    <span className="error-text active">{formErrors.postalCode}</span>
                  )}
                </div>
              </div>

              <div className="col-md-6">
                <div className="cp-split" style={{ position: "relative" }}>
                  <label className="fi-label">Phone No. <span>*</span></label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className={`common-input ${showError('phone') ? 'error' : ''}`}
                    maxLength={FIELD_MAX_LENGTHS.contact}
                  />
                  {showError('phone') && (
                    <span className="error-text active">{formErrors.phone}</span>
                  )}
                </div>
              </div>

              <div className="col-md-6">
                <div className="cp-split" style={{ position: "relative" }}>
                  <label className="fi-label">Company Email <span>*</span></label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className={`common-input ${showError('email') ? 'error' : ''}`}
                    maxLength={FIELD_MAX_LENGTHS.email}
                  />
                  {showError('email') && (
                    <span className="error-text active">{formErrors.email}</span>
                  )}
                </div>
              </div>
            </div>


            <div className="m-button"style={{ position: 'absolute', right: '25px', bottom: '25px', margin: '0' }}>
              <button type="submit" className="cp-button" disabled={loading}>
                {loading ? <Loader /> : 'Update & Continue'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CompanyProfileModal;
